import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type ApiDevice } from '../auth/api';
import { useAuthStore } from '../auth/store';

/**
 * Account-mode dashboard. Lists every trusted device, decorated with:
 *   * runtime `online` flag (green dot) from the server's RoomRegistry view,
 *   * inline-edit nickname (pencil → input → commit on blur / Enter),
 *   * remove confirmation,
 *   * link to /security for 2FA management.
 *
 * Everything is optimistic: rename and remove update local state before the server
 * acknowledges so the UI stays snappy on slow links. If the server rejects we re-fetch
 * the canonical list to undo the optimistic mutation.
 */
export function MachinesPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [devices, setDevices] = useState<ApiDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const { devices } = await api.listDevices(token);
      setDevices(devices);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        logout();
        navigate('/login', { replace: true });
      } else {
        setError('Failed to load machines');
      }
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    void reload();
  }, [token, navigate, reload]);

  const remove = useCallback(
    async (id: string) => {
      if (!token) return;
      if (!window.confirm('Remove this machine from your list? It can be re-added later with the ephemeral password.')) return;
      // Optimistic update.
      const prev = devices;
      setDevices((d) => (d ? d.filter((x) => x.id !== id) : d));
      try {
        await api.removeDevice(token, id);
      } catch {
        setError('Failed to remove machine');
        setDevices(prev);
      }
    },
    [token, devices],
  );

  const beginRename = (d: ApiDevice) => {
    setEditingId(d.id);
    setEditingValue(d.nickname ?? d.hostname ?? '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const commitRename = useCallback(
    async (id: string) => {
      if (!token) return;
      const trimmed = editingValue.trim();
      const nickname = trimmed.length > 0 ? trimmed : null;
      setEditingId(null);
      // Optimistic patch.
      setDevices((list) =>
        list ? list.map((d) => (d.id === id ? { ...d, nickname } : d)) : list,
      );
      try {
        await api.renameDevice(token, id, nickname);
      } catch (err) {
        setError(err instanceof ApiError && err.code === 'not_found'
          ? 'Machine no longer exists'
          : 'Failed to rename machine');
        // Reconcile with server state.
        void reload();
      }
    },
    [token, editingValue, reload],
  );

  return (
    <main className="min-h-screen p-4 sm:p-6 max-w-2xl mx-auto">
      {/* Three-row responsive header.
       *   row 1: ← back-to-home (always visible, 44px tap target).
       *   row 2: page title + signed-in-as subtitle (truncates on narrow screens).
       *   row 3: action chips (security, sign out) — wraps gracefully on phones.
       * Splitting into rows instead of `flex-wrap` on a single row keeps the back-arrow
       * pinned in the top-left where the user expects to find it, regardless of email
       * length or device width. */}
      <header className="mb-6 space-y-3">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)] transition-colors -ml-1 px-2 py-1.5 rounded"
          aria-label="Back to home"
        >
          <span aria-hidden="true">←</span>
          <span>home</span>
        </Link>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold leading-tight">My machines</h1>
            <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
              {user?.email ?? '...'}
              {user?.totp_enabled && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--color-accent)]/10 text-[var(--color-accent)] text-[10px]">
                  2FA on
                </span>
              )}
            </p>
          </div>
          <Link
            to="/"
            className="flex-none text-xs px-3 py-2 rounded border border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-black transition whitespace-nowrap"
          >
            + add machine
          </Link>
        </div>

        <nav
          aria-label="Account actions"
          className="flex items-center gap-2 text-xs"
        >
          <Link
            to="/security"
            className="px-3 py-1.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-border)] transition"
          >
            security
          </Link>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="px-3 py-1.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-border)] transition"
          >
            sign out
          </button>
        </nav>
      </header>

      {error && (
        <div className="mb-4 px-4 py-3 bg-[var(--color-danger)]/10 border border-[var(--color-danger)]/40 rounded text-sm text-[var(--color-danger)]">
          {error}
        </div>
      )}

      {devices === null && (
        <p className="text-sm text-[var(--color-text-muted)]">Loading…</p>
      )}
      {devices !== null && devices.length === 0 && (
        <div className="text-center py-12 border border-dashed border-[var(--color-border)] rounded">
          <p className="text-sm text-[var(--color-text-muted)] mb-2">No machines saved yet.</p>
          <Link to="/" className="text-sm text-[var(--color-accent)] hover:underline">
            Connect to one and click "Save this machine"
          </Link>
        </div>
      )}
      {devices !== null && devices.length > 0 && (
        <ul className="space-y-2">
          {devices.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-4 py-3"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Online indicator: live = bright green, recent (≤5min) = dim, idle = grey. */}
                <span
                  aria-label={d.online ? 'online' : 'offline'}
                  className={`flex-none w-2.5 h-2.5 rounded-full ${
                    d.online ? 'bg-emerald-400' : 'bg-[var(--color-border)]'
                  }`}
                />
                {editingId === d.id ? (
                  <input
                    type="text"
                    autoFocus
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={() => commitRename(d.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(d.id);
                      if (e.key === 'Escape') cancelRename();
                    }}
                    maxLength={64}
                    className="flex-1 bg-[var(--color-bg)] border border-[var(--color-accent)] rounded px-2 py-1 text-sm focus:outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(`/session/${d.host_id}`)}
                    className="text-left flex-1 group min-w-0"
                  >
                    <div className="font-medium group-hover:text-[var(--color-accent)] transition truncate">
                      {d.nickname || d.hostname || `Host ${d.host_id}`}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] mt-0.5 font-mono">
                      {d.host_id.slice(0, 3)} {d.host_id.slice(3, 6)} {d.host_id.slice(6, 9)} ·{' '}
                      {d.platform ?? '?'} · {d.online ? 'online' : `last seen ${formatRelative(d.last_seen_at)}`}
                    </div>
                  </button>
                )}
              </div>
              {editingId === d.id ? (
                <button
                  type="button"
                  onClick={cancelRename}
                  className="text-xs text-[var(--color-text-muted)] ml-3"
                >
                  cancel
                </button>
              ) : (
                <div className="flex items-center gap-3 ml-3">
                  <button
                    type="button"
                    onClick={() => beginRename(d)}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
                    aria-label="Rename machine"
                  >
                    rename
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(d.id)}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                    aria-label="Remove machine"
                  >
                    remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}
